import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";

export default class extends Controller {
  static targets = ["field", "input", "radioGroup", "error"];
  static values = { previewUrl: String };

  connect() {
    // <input type="hidden"> keeps its `value` IDL attribute in "default" mode, so
    // setting `.value` also moves `.defaultValue` — a JS-driven hidden field (e.g.
    // the calorie-override stepper) can never look dirty via defaultValue. Snapshot
    // hidden values at connect and diff against that instead.
    this.hiddenValues = new Map();
    this.element
      .querySelectorAll('input[type="hidden"]')
      .forEach((el) => this.hiddenValues.set(el, el.value));
  }

  /**
   * Validates the Calorie Details fields, then runs {@link confirm}.
   *
   * @param {MouseEvent} event click on the Calorie Details Save button
   */
  validateAndConfirm(event) {
    this.validate(event);
    if (event.defaultPrevented) return;

    this.confirm(event);
  }

  /**
   * Decides whether a Save needs the Confirm Changes modal named by
   * data-confirm-modal. A button marked data-confirm-on-change opens it when its
   * section changed. Otherwise the server preview decides: the modal opens only
   * when the save reprices the plan (including a dog that has aged into new
   * calories with nothing edited). With no modal named the form submits
   * normally, and a failed preview submits too, since the server reprices on
   * save either way.
   *
   * @param {MouseEvent} event click on a section's Save button
   */
  confirm(event) {
    const button = event.currentTarget;
    const modalId = button.dataset.confirmModal;
    const modalEl = modalId && document.getElementById(modalId);
    if (!modalEl) return;

    if (button.dataset.confirmOnChange === "true" && this.#sectionChanged(button)) {
      event.preventDefault();
      Modal.getOrCreateInstance(modalEl).show();
      return;
    }
    if (!this.hasPreviewUrlValue) return;

    event.preventDefault();
    this.#planChanges()
      .then((changes) => {
        if (changes) {
          Modal.getOrCreateInstance(modalEl).show();
        } else {
          this.element.submit();
        }
      })
      .catch(() => this.element.submit());
  }

  /**
   * @returns {Promise<boolean>} whether the pending form values reprice the plan
   */
  #planChanges() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = new FormData(this.element);
    body.delete("_method");
    return fetch(this.previewUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token, Accept: "application/json" },
      body,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => Boolean(data && data.interval_count));
  }

  // True when any input in the button's section-edit block differs from its
  // server-rendered default. Reads defaultValue/defaultChecked (not a JS snapshot)
  // so it's immune to other controllers mutating the DOM on connect.
  #sectionChanged(button) {
    const section = button.closest("[data-controller~='section-edit']");
    if (!section) return true;
    return Array.from(section.querySelectorAll("input, select, textarea")).some((el) =>
      this.#inputDirty(el)
    );
  }

  #inputDirty(el) {
    if (el.type === "radio" || el.type === "checkbox") return el.checked !== el.defaultChecked;
    if (el.tagName === "SELECT") return Array.from(el.options).some((o) => o.selected !== o.defaultSelected);
    if (el.type === "hidden") return this.hiddenValues.get(el) !== el.value;
    return el.value !== el.defaultValue;
  }

  validate(event) {
    let valid = true;

    this.fieldTargets.forEach((fieldEl) => {
      const input = fieldEl.querySelector("[data-dog-edit-validation-target='input']");
      const radioGroup = fieldEl.querySelector("[data-dog-edit-validation-target='radioGroup']");
      const error = fieldEl.querySelector("[data-dog-edit-validation-target='error']");

      let fieldValid = true;

      if (input) {
        fieldValid = input.value.trim() !== "";
        this.#toggleError(input, error, fieldValid);
      } else if (radioGroup) {
        const radios = radioGroup.querySelectorAll("input[type='radio']");
        fieldValid = Array.from(radios).some((r) => r.checked);
        this.#toggleError(radioGroup.querySelector(".account-radio-input__container") || radioGroup, error, fieldValid);
      }

      if (!fieldValid) valid = false;
    });

    if (!valid) {
      event.preventDefault();
    }
  }

  #toggleError(el, errorEl, valid) {
    el.setAttribute("aria-invalid", !valid);

    if (valid) {
      el.style.borderColor = "";
      if (errorEl) errorEl.classList.add("d-none");
    } else {
      el.style.borderColor = "#dc3545";
      if (errorEl) errorEl.classList.remove("d-none");
    }
  }
}
